import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronLeftIcon, LoaderIcon } from '@/components/icons';
import { agentApi } from '@/api/agent.api';
import { fullErrorText } from '@/api/errors';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { DescriptionText } from '@/components/ui/description-text';
import { applicationApi } from '@/api/application.api';
import { sandboxClassApi } from '@/api/sandboxClass.api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { create } from '@bufbuild/protobuf';
import { InstanceSchema } from '@/proto/agentplatform/v1/instance_pb';
import { ChannelType, ChannelAccountBindingMode } from '@/proto/agentplatform/v1/channel_pb';
import type { Param } from '@/proto/agentplatform/v1/param_pb';
import type { SandboxClass } from '@/proto/agentplatform/v1/sandbox_pb';
import ChannelParamInput from '@/components/Agents/ChannelParamInput';
import { CHANNEL_PARAM_DEFINITIONS } from '@/components/Agents/agentFormTypes';
import FaultToleranceFields from '@/components/Agents/FaultToleranceFields';
import {
  buildFaultToleranceSlotParams,
  parseFaultToleranceConfig,
  validateFaultTolerance,
} from '@/components/Agents/faultTolerance';
import { maasApi } from '@/api/maas.api';
import { groupModels } from '@/utils/model-display';
import {
  PLUGIN_TYPE_SANDBOX,
  PLUGIN_TYPE_SKILLGROUP,
  PLUGIN_TYPE_RAG,
  PLUGIN_TYPE_MEMORY,
  PLUGIN_TYPE_INFINI_MEMORY,
  PLUGIN_TYPE_MCP,
  PLUGIN_TYPE_TOOLKIT,
  PLUGIN_TYPE_USER_TOOLKIT,
  PLUGIN_TYPE_APPLICATION,
  PLUGIN_TYPE_SANDBOX_CLASS,
  PLUGIN_TYPE_SKILLGROUP_CLASS,
  PLUGIN_TYPE_RAG_CLASS,
  PLUGIN_TYPE_MEMORY_CLASS,
  PLUGIN_TYPE_MCP_CLASS
} from '@/constants/plugin'

const HIDDEN_PLUGIN_PARAM_NAMES = new Set(['interaction_mode', 'infini_memory_prefix'])

function isUserVisiblePluginParam(param: Param): boolean {
  return !HIDDEN_PLUGIN_PARAM_NAMES.has(param.name)
}

function IconBox() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
      />
    </svg>
  )
}

function IconBrain() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  )
}

function IconSliders() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
      />
    </svg>
  )
}

function SectionCard({
  id,
  icon,
  title,
  subtitle,
  children
}: {
  id: string
  icon: React.ReactNode
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <section id={id} className="scroll-mt-20">
      <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
        <div className="h-0.5 bg-gradient-to-r from-primary/80 via-primary/40 to-transparent" />
        <div className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">
              {icon}
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">{title}</h2>
              {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {children}
        </div>
      </div>
    </section>
  )
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  )
}

function ParamInput({
  param,
  value,
  onChange
}: {
  param: Param
  value: string
  onChange: (val: string) => void
}) {
  const normalizedType = (param.type || '').toLowerCase()
  const isBooleanType = normalizedType === 'bool' || normalizedType === 'boolean'
  const isArrayType = normalizedType === 'array' || normalizedType.endsWith('[]')
  const defaultValue = param.paramValue?.value || ''

  const toArrayEditorText = (raw: string): string => {
    const source = (raw || defaultValue || '').trim()
    if (!source) {
      return ''
    }
    if (source.startsWith('[')) {
      try {
        const arr = JSON.parse(source)
        if (Array.isArray(arr)) {
          return arr.map(item => String(item)).join('\n')
        }
      } catch {
        // fallback to plain split
      }
    }
    return source
      .split(/[\n,]/)
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .join('\n')
  }

  const fromArrayEditorText = (raw: string): string => {
    const items = raw
      .split('\n')
      .map(item => item.trim())
      .filter(item => item.length > 0)
    return JSON.stringify(items)
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{param.name || param.target}</Label>
        <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
          {param.type}
        </span>
      </div>
      {isBooleanType ? (
        <select
          value={value || defaultValue || ''}
          onChange={e => onChange(e.target.value)}
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">请选择</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : isArrayType ? (
        <>
          <textarea
            placeholder="每行输入一个值"
            value={toArrayEditorText(value)}
            onChange={e => onChange(fromArrayEditorText(e.target.value))}
            rows={3}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
          />
          <p className="text-[10px] text-muted-foreground">每行一个元素，系统会自动保存为数组</p>
        </>
      ) : (
        <Input
          placeholder={defaultValue || '请输入值...'}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="h-9 text-sm font-mono"
        />
      )}
    </div>
  )
}

function JsonParamInput({
  label,
  value,
  onChange,
  readOnly = false
}: {
  label: string
  value: string
  onChange: (val: string) => void
  readOnly?: boolean
}) {
  const [jsonError, setJsonError] = useState<string | null>(null)
  const lineCount = (value || '').split('\n').length

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    onChange(val)
    if (!val.trim()) {
      setJsonError(null)
      return
    }
    try {
      JSON.parse(val)
      setJsonError(null)
    } catch (err) {
      setJsonError((err as Error).message)
    }
  }

  const handleFormat = () => {
    if (!value.trim()) return
    try {
      const parsed = JSON.parse(value)
      onChange(JSON.stringify(parsed, null, 2))
      setJsonError(null)
    } catch {
      // keep current value
    }
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">{label}</Label>
          <span className="text-[10px] font-mono text-muted-foreground px-1.5 py-0.5 rounded bg-muted">
            JSON
          </span>
          {readOnly && (
            <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 rounded bg-muted">
              只读
            </span>
          )}
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={handleFormat}
            disabled={!!jsonError || !value.trim()}
            className="text-xs text-primary hover:text-primary/80 disabled:text-muted-foreground disabled:cursor-not-allowed"
          >
            格式化
          </button>
        )}
      </div>
      <div
        className={`rounded-md border overflow-hidden ${jsonError ? 'border-destructive' : 'border-input'}`}
      >
        <div className="flex bg-zinc-950">
          <div
            className="select-none text-right pr-3 pl-3 py-3 text-xs font-mono leading-[1.625rem] text-zinc-600 bg-zinc-900/50 border-r border-zinc-800"
            aria-hidden="true"
          >
            {Array.from({ length: Math.max(lineCount, 6) }, (_, i) => (
              <div key={i}>{i + 1}</div>
            ))}
          </div>
          <textarea
            placeholder="{}"
            value={value}
            onChange={handleChange}
            readOnly={readOnly}
            rows={Math.max(lineCount, 6)}
            spellCheck={false}
            className={`flex-1 bg-zinc-950 text-zinc-100 px-3 py-3 text-sm font-mono leading-[1.625rem] ring-offset-background placeholder:text-zinc-600 focus-visible:outline-none resize-none w-0 ${readOnly ? 'cursor-default opacity-70' : ''}`}
          />
        </div>
      </div>
      {jsonError && <p className="text-xs text-destructive">{jsonError}</p>}
    </div>
  )
}

function channelTypeLabel(channelType: ChannelType): string {
  switch (channelType) {
    case ChannelType.FEISHU:
      return '飞书'
    case ChannelType.QQ:
      return 'QQ'
    default:
      return ChannelType[channelType] ?? '未知渠道'
  }
}

function pluginTypeLabel(pluginType: string): string {
  switch (pluginType) {
    case PLUGIN_TYPE_SANDBOX:
      return '沙箱实例'
    case PLUGIN_TYPE_SANDBOX_CLASS:
      return '沙箱模版'
    case PLUGIN_TYPE_SKILLGROUP:
      return '技能组实例'
    case PLUGIN_TYPE_SKILLGROUP_CLASS:
      return '技能组模版'
    case PLUGIN_TYPE_RAG:
      return '知识库实例'
    case PLUGIN_TYPE_RAG_CLASS:
      return '知识库模版'
    case PLUGIN_TYPE_MEMORY:
      return '记忆实例'
    case PLUGIN_TYPE_INFINI_MEMORY:
      return 'infini-memory'
    case PLUGIN_TYPE_MEMORY_CLASS:
      return '记忆模版'
    case PLUGIN_TYPE_MCP:
      return 'MCP 实例'
    case PLUGIN_TYPE_MCP_CLASS:
      return 'MCP 模版'
    case PLUGIN_TYPE_TOOLKIT:
      return '平台工具包'
    case PLUGIN_TYPE_USER_TOOLKIT:
      return '用户工具包'
    case PLUGIN_TYPE_APPLICATION:
      return '子代理'
    default:
      return pluginType
  }
}

function pluginTypeBadgeClass(pluginType: string): string {
  switch (pluginType) {
    case PLUGIN_TYPE_SANDBOX:
    case PLUGIN_TYPE_SANDBOX_CLASS:
      return 'bg-blue-500/10 text-blue-600'
    case PLUGIN_TYPE_SKILLGROUP:
    case PLUGIN_TYPE_SKILLGROUP_CLASS:
      return 'bg-orange-500/10 text-orange-600'
    case PLUGIN_TYPE_RAG:
    case PLUGIN_TYPE_RAG_CLASS:
      return 'bg-cyan-500/10 text-cyan-600'
    case PLUGIN_TYPE_MEMORY:
    case PLUGIN_TYPE_MEMORY_CLASS:
    case PLUGIN_TYPE_INFINI_MEMORY:
      return 'bg-pink-500/10 text-pink-600'
    case PLUGIN_TYPE_MCP:
    case PLUGIN_TYPE_MCP_CLASS:
      return 'bg-indigo-500/10 text-indigo-600'
    case PLUGIN_TYPE_TOOLKIT:
      return 'bg-green-500/10 text-green-600'
    case PLUGIN_TYPE_USER_TOOLKIT:
      return 'bg-purple-500/10 text-purple-600'
    case PLUGIN_TYPE_APPLICATION:
      return 'bg-amber-500/10 text-amber-600'
    default:
      return 'bg-gray-500/10 text-gray-600'
  }
}

export default function CreateAgentInstancePage() {
  const { uid } = useParams<{ uid: string }>()
  const navigate = useNavigate()

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [pluginClassDetails, setPluginClassDetails] = useState<Record<string, SandboxClass>>({});
  const [ftForm, setFtForm] = useState(() => parseFaultToleranceConfig(undefined));

  const { data: agentData, isLoading: agentLoading } = useQuery({
    queryKey: ['agent', uid],
    queryFn: () => agentApi.getAgent(uid!),
    enabled: !!uid
  })

  const ftConfigurable = agentData?.advancedConfig?.faultTolerance?.configurable === true;

  // 备用模型下拉：复用模板凭证的模型列表
  const ftModelsQuery = useQuery({
    queryKey: ['maas-credential-models', agentData?.modelConfig?.apiKeyRef],
    queryFn: () => maasApi.listModelsByCredential(agentData!.modelConfig!.apiKeyRef),
    enabled: ftConfigurable && Boolean(agentData?.modelConfig?.apiKeyRef),
  });
  const ftGroupedModels = useMemo(
    () => groupModels(ftModelsQuery.data?.items ?? []),
    [ftModelsQuery.data]
  );

  useEffect(() => {
    if (agentData) {
      const defaults: Record<string, string> = {}

      agentData.modelConfig?.modelParams?.forEach(p => {
        if (p.configurable && p.name) defaults[p.name] = p.paramValue?.value || ''
      })

      agentData.channelConfigs?.forEach(channel => {
        channel.channelParams?.forEach(p => {
          if (p.configurable && p.name) defaults[p.name] = p.paramValue?.value || ''
        })
      })

      agentData.pluginConfigs?.forEach(plugin => {
        plugin.pluginParams?.forEach(p => {
          if (p.configurable && p.name) defaults[p.name] = p.paramValue?.value || ''
        })
      })

      setParamValues(defaults)

      const tmplFt = agentData.advancedConfig?.faultTolerance;
      if (tmplFt?.configurable) {
        setFtForm(parseFaultToleranceConfig(tmplFt));
      }

      const classUids = (agentData.pluginConfigs || [])
        .filter(p => p.pluginType === 'sandboxClass' && p.pluginUid)
        .map(p => p.pluginUid)

      if (classUids.length > 0) {
        Promise.all(
          classUids.map(id => sandboxClassApi.getSandboxClass(id).catch(() => null))
        ).then(results => {
          const details: Record<string, SandboxClass> = {}
          results.forEach((data, i) => {
            if (data) details[classUids[i]] = data
          })
          setPluginClassDetails(details)
        })
      } else {
        setPluginClassDetails({})
      }
    }
  }, [agentData])

  const createMutation = useMutation({
    mutationFn: applicationApi.create,
    onSuccess: () => navigate('/agents?tab=instance'),
    onError: error => toast.error(`创建失败：${fullErrorText(error)}`)
  })

  const handleSubmit = () => {
    if (!uid || !name) return;
    const finalParams = { ...paramValues };
    if (ftConfigurable) {
      const tmplEnabled = agentData?.advancedConfig?.faultTolerance?.enabled ?? false;
      const ftToSave = { ...ftForm, enabled: tmplEnabled };
      const err = validateFaultTolerance(ftToSave);
      if (err) {
        toast.error(err);
        return;
      }
      // 实例只持久化三个运行时槽位 key，运行期按槽位覆盖模板默认
      Object.assign(finalParams, buildFaultToleranceSlotParams(ftToSave));
    }
    createMutation.mutate(
      create(InstanceSchema, {
        name,
        description,
        agentId: uid,
        params: finalParams,
      })
    )
  }

  const isPending = createMutation.isPending

  const allModelParams = (agentData?.modelConfig?.modelParams || []).filter(p => p.configurable)
  const systemPromptParam = allModelParams.filter(p => p.target === 'model_config.system_prompt')
  const modelParams = allModelParams.filter(
    p => !(p.target || '').startsWith('compression.') && p.target !== 'model_config.system_prompt'
  )
  const channelConfigs = (agentData?.channelConfigs || [])
    .filter(c => c.bindingMode !== ChannelAccountBindingMode.TEMPLATE)
    .map(c => ({
      ...c,
      channelParams: (c.channelParams || []).filter(p => p.configurable)
    }))
  const pluginConfigs = (agentData?.pluginConfigs || []).map(p => ({
    ...p,
    pluginParams: (p.pluginParams || []).filter(param => param.configurable)
  }))
  const hasParams =
    modelParams.length > 0 ||
    systemPromptParam.length > 0 ||
    channelConfigs.some(channel => (channel.channelParams?.length || 0) > 0) ||
    pluginConfigs.some(p => p.pluginParams?.length > 0)

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="w-full px-6 pt-8 pb-4">
        <button
          onClick={() => navigate(`/agents/${uid}`)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          返回智能体模版详情
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">创建智能体实例</h1>
            <p className="text-sm text-muted-foreground mt-1">
              从智能体模版「{agentData?.name || uid}」创建实例
            </p>
          </div>
        </div>
      </div>

      <div className="w-full px-6 pb-16">
        <div className="space-y-5">
          <SectionCard id="basic" icon={<IconBox />} title="实例信息" subtitle="填写实例基本信息">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm font-medium">
                  实例名称 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="例如: 客服机器人-生产环境"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="h-10"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="desc" className="text-sm font-medium">
                  描述
                </Label>
                <ExpandableTextarea
                  id="desc"
                  placeholder="简要描述此实例的用途..."
                  value={description}
                  onChange={setDescription}
                  collapsedRows={2}
                />
              </div>
            </div>
          </SectionCard>

          {hasParams && (
            <SectionCard
              id="params"
              icon={<IconSliders />}
              title="参数配置"
              subtitle="配置模版定义的参数值"
            >
              <div className="space-y-6">
                {modelParams.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground">模型参数</h3>
                    <div className="space-y-3">
                      {modelParams.map(param => (
                        <ParamInput
                          key={`model.${param.name || 'unknown'}`}
                          param={param}
                          value={paramValues[param.name || ''] || ''}
                          onChange={val =>
                            setParamValues(prev => ({ ...prev, [param.name || '']: val }))
                          }
                        />
                      ))}
                    </div>
                  </div>
                )}

                {systemPromptParam.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground">系统提示词</h3>
                    <div className="space-y-3">
                      {systemPromptParam.map(param => (
                        <div key="system_prompt">
                          <ExpandableTextarea
                            placeholder={param.paramValue?.value || '请输入系统提示词...'}
                            value={paramValues['system_prompt'] || ''}
                            onChange={v =>
                              setParamValues(prev => ({
                                ...prev,
                                system_prompt: v
                              }))
                            }
                            collapsedRows={4}
                            markdownPreview
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {channelConfigs
                  .filter(channel => (channel.channelParams?.length || 0) > 0)
                  .map((channel, channelIndex) => (
                    <div key={`channel.${channelIndex}`} className="space-y-3">
                      <h3 className="text-sm font-semibold text-foreground">
                        渠道参数（{channelTypeLabel(channel.channelType)}）
                      </h3>
                      <div className="space-y-3">
                        {channel.channelParams.map(param => {
                          const paramDefs = CHANNEL_PARAM_DEFINITIONS[channel.channelType] ?? []
                          const paramDef = paramDefs.find(d => d.name === param.name)
                          if (paramDef) {
                            return (
                              <ChannelParamInput
                                key={`channel.${channelIndex}.${param.name || 'unknown'}`}
                                def={paramDef}
                                value={paramValues[param.name || ''] || ''}
                                onChange={val =>
                                  setParamValues(prev => ({
                                    ...prev,
                                    [param.name || '']: val
                                  }))
                                }
                              />
                            )
                          }
                          return (
                            <ParamInput
                              key={`channel.${channelIndex}.${param.name || 'unknown'}`}
                              param={param}
                              value={paramValues[param.name || ''] || ''}
                              onChange={val =>
                                setParamValues(prev => ({
                                  ...prev,
                                  [param.name || '']: val
                                }))
                              }
                            />
                          )
                        })}
                      </div>
                    </div>
                  ))}

                {pluginConfigs.map((plugin, idx) =>
                  plugin.pluginParams?.some(isUserVisiblePluginParam) ? (
                    <div key={idx} className="space-y-3">
                      <div className="pb-2 border-b border-border/50">
                        <div className="flex items-center gap-2">
                          <span
                            className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-medium ${pluginTypeBadgeClass(plugin.pluginType)}`}
                          >
                            {pluginTypeLabel(plugin.pluginType)}
                          </span>
                          <span className="text-sm font-semibold text-foreground">
                            {pluginClassDetails[plugin.pluginUid]?.name ||
                              plugin.name ||
                              plugin.pluginType}
                          </span>
                        </div>
                        {plugin.pluginType === 'sandboxClass' &&
                          pluginClassDetails[plugin.pluginUid]?.description && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {pluginClassDetails[plugin.pluginUid].description}
                            </p>
                          )}
                      </div>
                      <div className="space-y-3">
                        {plugin.pluginType === PLUGIN_TYPE_USER_TOOLKIT
                          ? plugin.pluginParams.filter(isUserVisiblePluginParam).map(param => (
                              <JsonParamInput
                                key={param.name}
                                label={param.name || '工具包配置'}
                                value={paramValues[param.name || ''] || ''}
                                readOnly={!param.configurable}
                                onChange={val =>
                                  setParamValues(prev => ({
                                    ...prev,
                                    [param.name || '']: val
                                  }))
                                }
                              />
                            ))
                          : plugin.pluginParams.filter(isUserVisiblePluginParam).map(param => (
                              <ParamInput
                                key={param.name}
                                param={param}
                                value={paramValues[param.name || ''] || ''}
                                onChange={val =>
                                  setParamValues(prev => ({
                                    ...prev,
                                    [param.name || '']: val
                                  }))
                                }
                              />
                            ))}
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            </SectionCard>
          )}

          {ftConfigurable && (
            <SectionCard
              id="faultTolerance"
              icon={<IconSliders />}
              title="容错配置"
              subtitle="模板已开放容错配置，可为本实例调整（是否启用容错继承自模板）"
            >
              <FaultToleranceFields
                value={ftForm}
                onChange={setFtForm}
                groupedModels={ftGroupedModels}
                hideEnabledToggle
              />
            </SectionCard>
          )}

          <SectionCard
            id="template"
            icon={<IconBrain />}
            title="模版信息"
            subtitle="来自智能体模版的配置（只读）"
          >
            <div className="space-y-1">
              <InfoRow label="模版名称" value={agentData?.name || '-'} />
              <InfoRow
                label="描述"
                value={
                  <DescriptionText text={agentData?.description || ''} maxChars={40} markdown />
                }
              />
              <InfoRow label="模型" value={agentData?.modelConfig?.modelName || '-'} />
              <InfoRow
                label="渠道"
                value={
                  agentData?.channelConfigs?.length
                    ? agentData.channelConfigs
                        .map(item => channelTypeLabel(item.channelType))
                        .join(' / ')
                    : '-'
                }
              />
              <InfoRow label="插件数" value={`${agentData?.pluginConfigs?.length || 0} 个`} />
            </div>
          </SectionCard>

          <div className="pt-4">
            <Button
              onClick={handleSubmit}
              disabled={isPending || agentLoading || !name}
              className="w-full h-11 gap-2 text-base"
            >
              {isPending ? (
                <>
                  <LoaderIcon className="h-4 w-4 animate-spin" />
                  创建中...
                </>
              ) : (
                '创建实例'
              )}
            </Button>
            {!name && (
              <p className="text-xs text-muted-foreground text-center mt-2">请填写实例名称</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
